import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../../store/rootReducer";
import "./LightCalculator.css";
import InputField from "../genericComponents/InputField/InputField";
import NfsDropdown from "../genericComponents/NfsDropdown/NfsDropdown";
import {
    clearLightInMap,
    featureHasLight,
    formatRangeNm,
    formatRangeOfRangeNm,
    getCharacterColors,
    getDomainCodeFromValue,
    getFeatureTable,
    getShortestActiveTime,
    lightCalculationIncandescent,
    lightCalculationLED,
    renderLightInMap,
} from "./lightCalculationUtils";
import {
    applyEdits,
    applyLabelsToNFSLayers,
    findAndSelectFeature,
    GetCodedValueAndAlias,
    getMapView,
    getRelatedFeatures,
    highlightFeature,
    removeLabelsFromNFSLayers,
} from "../../utils/arcgisUtils";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import {
    BackgroundLight,
    LensOrder,
    lensOrderStringToNumber,
    LightCalculationLightColors,
    LightCalculationResult,
    LightType,
    PassThroughMaterial,
} from "../../constants/LightCalculation";
import { beregnMin, findRange, lightCalculationCandela, omvFixIntensity } from "./lightCalculationCandelaUtils";
import { AppContext, UserContext } from "../../Context";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import { canEditVersion } from "../../utils/versioningUtils";
import RadioButtonGroup from "../genericComponents/RadioButton/RadioButtonGroup";
import Point from "@arcgis/core/geometry/Point";
import { metersPerNauticalMile } from "../../utils/constants";
import FeatureViewerTitle from "../FeatureViewer/FeatureViewerTitle";
import { AppConfig, LayersConfig } from "../../AppConfig";
import * as symbolUtils from "@arcgis/core/symbols/support/symbolUtils";
import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Toggle from "../genericComponents/Toggle/Toggle";
import CloseIcon from "../../assets/close.svg?react";
import ReturnIcon from "../../assets/arrow-back.svg?react";
import { changeRightPanelTab, setHasUnsavedChanges, setLightCalculatorOpen } from "../../store/appSlice";
import { RightPanelTabs } from "../../constants/Panels";
import { getNewStatus, NFSStatus } from "../../utils/nfsUtils";
import FeatureWorkOrderGroup from "../FeatureViewer/FeatureWorkOrders/FeatureWorkOrderGroup";
import Loader from "../genericComponents/Loader/Loader";
import { updateFDVObjectNew, updateFDVWorkOrdersWithLightParams } from "../../utils/FDVUtils";
import { toast } from "react-toastify";
import { AppDispatch } from "../../store/storeSetup";
import { Navlystype, SeamarkColors } from "../../constants/nfsDomains";
import { getMapSettings } from "../../utils/localStorageUtils";
import { resolveLabelsToApply } from "../../utils/labelSettings";
import type { ResourceHandle } from "@arcgis/core/core/Handles";
import type Graphic from "@arcgis/core/Graphic";

// There are rules which should only apply when sjomerke_type is one of the cardinal directions
const listOfKardinals = new Set([21, 22, 23, 24]); // N, S, E, W

const LightCalculator = () => {
    const dispatch = useDispatch<AppDispatch>();

    const [symbolHtmlElement, setSymbolHtmlElement] = useState<HTMLElement | null>(null);
    const [showRangeInMap, setShowRangeInMap] = useState<boolean>(false);
    const [selectedWorkOrderIds, setSelectedWorkOrderIds] = useState<string[]>([]);

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [canIEditVersion, setCanIEditVersion] = useState<boolean>(false);
    const [activeUnit, setActiveUnit] = useState<"Nm" | "m">("Nm");

    const [desiredDistanceDynamicInput, setDesiredDistanceDynamicInput] = useState<string>("");
    const [desiredDistance, setDesiredDistance] = useState<number>(0);
    const [shortestActiveTime, setShortestActiveTime] = useState<string>("");
    const [backgroundLight, setBackgroundLight] = useState<BackgroundLight>(BackgroundLight.NONE);
    const [lightColor, setLightColor] = useState<LightCalculationLightColors | null>(null);
    const [passthroughMaterial, setPassthroughMaterial] = useState<PassThroughMaterial>(PassThroughMaterial.NONE);
    const [lensOrder, setLensOrder] = useState<LensOrder | null>(null);
    const [whiteMaxCandela, setWhiteMaxCandela] = useState<number>(0);
    const [whiteMinCandela, setWhiteMinCandela] = useState<number>(0);
    const [redMaxCandela, setRedMaxCandela] = useState<number>(0);
    const [redMinCandela, setRedMinCandela] = useState<number>(0);
    const [greenMinCandela, setGreenMinCandela] = useState<number>(0);
    const [greenMaxCandela, setGreenMaxCandela] = useState<number>(0);
    const [yellowMinCandela, setYellowMinCandela] = useState<number>(0);
    const [yellowMaxCandela, setYellowMaxCandela] = useState<number>(0);
    const [rangeWhite, setRangeWhite] = useState<string>("");
    const [rangeRed, setRangeRed] = useState<string>("");
    const [rangeGreen, setRangeGreen] = useState<string>("");
    const [rangeYellow, setRangeYellow] = useState<string>("");
    const [resultCandela, setResultCandela] = useState<{
        effectiveIntensity: number;
        maxIntensity: number;
        imin: number;
        message: string;
    }>({ effectiveIntensity: 0, maxIntensity: 0, imin: 0, message: "" });

    const [intensityWhite, setIntensityWhite] = useState<number>(0);
    const [percentageWhite, setPercentageWhite] = useState<number>(100);
    const [intensityRed, setIntensityRed] = useState<number>(0);
    const [percentageRed, setPercentageRed] = useState<number>(100);
    const [intensityGreen, setIntensityGreen] = useState<number>(0);
    const [percentageGreen, setPercentageGreen] = useState<number>(100);
    const [intensityYellow, setIntensityYellow] = useState<number>(0);
    const [percentageYellow, setPercentageYellow] = useState<number>(100);
    const [intensityIncandescent, setIntensityIncandescent] = useState<number>(0);

    const [calculationResults, setCalculationResults] = useState<LightCalculationResult>();
    const [candelaCL, setCandelaCL] = useState<Graphic[]>([]);
    const [relevantCandelaCLs, setRelevantCandelaCLs] = useState<Graphic[]>([]);
    const [selectedCandela, setSelectedCandela] = useState<Graphic | null>(null);
    const [characterCL, setCharacterCL] = useState<Graphic[]>([]);
    const [relevantCharacterCLs, setRelevantCharacterCLs] = useState<Graphic[]>([]);
    const [selectedCharacter, setSelectedCharacter] = useState<Graphic | null>(null);
    const [relevantCharacterTimes, setRelevantCharacterTimes] = useState<Graphic[]>([]);
    const previousSelectedFeatureGlobalIdRef = useRef<string | null>(null);
    const isLoadingFromFeature = useRef<boolean>(false);
    const layerConfig = useRef<LayersConfig | undefined>(undefined);
    const highlightHandlesRef = useRef<ResourceHandle[]>([]);

    const [lockedInColor, setLockedInColor] = useState<number | null>(null);
    const [colorsToShow, setColorsToShow] = useState<LightCalculationLightColors[]>([]);

    const lightCalculatorOpen = useSelector((state: StoreState) => state.app.lightCalculatorOpen);
    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);
    const versionsSharedWithMe = useSelector((state: StoreState) => state.version.versionsSharedWithMe);

    const rightPanelActiveTab = useSelector((state: StoreState) => state.app.rightPanelActiveTab);

    const hasUnsavedChanges = useSelector((state: StoreState) => state.app.hasUnsavedChanges);

    const userContext = useContext(UserContext);
    const appContext = useContext(AppContext);
    const selectedFeature = appContext?.selectedFeature.value;

    const [selectedFeatureRelatedMerke, setSelectedFeatureRelatedMerke] = useState<Graphic | null>(null);

    const filterCharacterCL = () => {
        const activeCharacters = characterCL.filter((character) => character.attributes.aktiv === 1);

        let featureFilteredCharacters = activeCharacters;
        if (selectedFeature && featureHasLight(selectedFeature)) {
            let lightType = selectedFeature.attributes.lys_type;
            if (lightType) {
                if (lightType === Navlystype.Overettlys) {
                    lightType = Navlystype.Lanterne;
                }
                featureFilteredCharacters = featureFilteredCharacters.filter(
                    (c) => c.attributes.lys_type_1 === lightType
                );
            }

            if (
                selectedFeature?.attributes?.lys_farge !== null &&
                selectedFeature?.attributes?.lys_farge !== undefined &&
                featureFilteredCharacters
            ) {
                const lightColorCode = selectedFeature.attributes.lys_farge;
                if (lightColorCode !== undefined && lightColorCode !== null && lightColorCode !== 0) {
                    featureFilteredCharacters = featureFilteredCharacters.filter(
                        (c) => c.attributes.lys_farge === lightColorCode
                    );
                }
            }
        }

        const activeValidFeatureFilteredCharacters = featureFilteredCharacters?.filter((character) => {
            let shouldShow = character.attributes.aktiv === 1;
            if (!shouldShow) {
                return false;
            }
            let klasseValid = true;
            let kardinalValid = true;

            if (selectedFeature && featureHasLight(selectedFeature)) {
                const featureKlasse = selectedFeature.attributes.klasse;
                const characterValidKlasse = character.attributes.tilordnet_klasse as string;
                if (featureKlasse && characterValidKlasse) {
                    const validClasses = characterValidKlasse.replaceAll(" ", "").split(",");
                    if (!validClasses.includes(featureKlasse.toString())) {
                        klasseValid = false;
                    }
                }
            }

            if (selectedFeature && featureHasLight(selectedFeature) && selectedFeatureRelatedMerke) {
                const featureKardinal = selectedFeatureRelatedMerke.attributes.sjomerke_type;
                const characterValidKardinal = character.attributes.tilordnet_kardinal;
                if (featureKardinal && characterValidKardinal) {
                    if (featureKardinal !== characterValidKardinal) {
                        kardinalValid = false;
                    }
                }
                if (listOfKardinals.has(featureKardinal) && !characterValidKardinal) {
                    kardinalValid = false;
                }
            }

            if (!klasseValid || !kardinalValid) {
                shouldShow = false;
            }

            return shouldShow;
        });

        if (activeValidFeatureFilteredCharacters && lockedInColor) {
            const colorFilteredCharacters = activeValidFeatureFilteredCharacters?.filter(
                (c) => c.attributes.lys_farge === lockedInColor
            );

            const uniqueColorFilteredCharacters = colorFilteredCharacters.filter(
                (character, index, self) =>
                    index === self.findIndex((c) => c.attributes.kode === character.attributes.kode)
            );

            setRelevantCharacterCLs(uniqueColorFilteredCharacters ?? []);
        } else if (activeValidFeatureFilteredCharacters) {
            const uniqueActiveFilteredCharacters = activeValidFeatureFilteredCharacters.filter(
                (character, index, self) =>
                    index === self.findIndex((c) => c.attributes.kode === character.attributes.kode)
            );

            setRelevantCharacterCLs(uniqueActiveFilteredCharacters ?? []);
        }
    };

    const resetAllValues = (allValues: boolean) => {
        setWhiteMaxCandela(0);
        setWhiteMinCandela(0);
        setRedMaxCandela(0);
        setRedMinCandela(0);
        setGreenMaxCandela(0);
        setGreenMinCandela(0);
        setYellowMaxCandela(0);
        setYellowMinCandela(0);
        setRangeWhite("");
        setRangeRed("");
        setRangeGreen("");
        setResultCandela({ effectiveIntensity: 0, maxIntensity: 0, imin: 0, message: "" });

        setIntensityWhite(0);
        setIntensityRed(0);
        setIntensityGreen(0);
        setIntensityIncandescent(0);

        setCalculationResults(undefined);

        if (allValues) {
            setDesiredDistance(0);
            setDesiredDistanceDynamicInput("");
            setPercentageWhite(100);
            setPercentageRed(100);
            setPercentageGreen(100);
            setPercentageYellow(100);
            setColorsToShow([]);
            setLightColor(null);
            setLockedInColor(null);
            setSelectedCandela(null);
            setSelectedCharacter(null);
            setBackgroundLight(BackgroundLight.NONE);
            setPassthroughMaterial(PassThroughMaterial.NONE);
            setLensOrder(null);

            filterCharacterCL();
        }
    };

    const resetAndLoadValuesFromSelectedFeature = (fromFeature: boolean) => {
        setHasUnsavedChangesTrue();
        if (fromFeature) {
            isLoadingFromFeature.current = true;
            resetAllValues(false);
            setSelectedCandela(null);
            setSelectedCharacter(null);

            // Using a short timeout to ensure all fields are wiped and then loaded
            setTimeout(() => {
                if (selectedFeature && featureHasLight(selectedFeature)) {
                    const lightColor = selectedFeature.attributes.lys_farge;
                    if (lightColor !== null && lightColor !== undefined && lightColor !== 0) {
                        const lightColorString = GetCodedValueAndAlias(
                            selectedFeature.layer as FeatureLayer,
                            "lys_farge",
                            lightColor
                        )[1].toString();
                        setLightColor(lightColorString as LightCalculationLightColors);
                        setLockedInColor(Number.parseInt(lightColor));
                    } else if (selectedFeature.attributes.klasse === 3 || selectedFeature.attributes.klasse === 4) {
                        setLockedInColor(0); // Multiple colors
                        setLightColor(null);
                    } else {
                        setLightColor(null);
                        setLockedInColor(null);
                    }

                    const tempPassthroughMaterial = selectedFeature.attributes.lys_gjennomgang;
                    if (tempPassthroughMaterial !== null && tempPassthroughMaterial !== undefined) {
                        const passthroughMaterialValue = GetCodedValueAndAlias(
                            selectedFeature.layer,
                            "lys_gjennomgang",
                            tempPassthroughMaterial
                        )[1].toString() as PassThroughMaterial;
                        setPassthroughMaterial(passthroughMaterialValue);
                    }
                    const lensOrderValue = selectedFeature.attributes.linse_orden;
                    if (lensOrderValue !== null && lensOrderValue !== undefined) {
                        const lensOrderString = GetCodedValueAndAlias(
                            selectedFeature.layer,
                            "linse_orden",
                            lensOrderValue
                        )[1].toString() as LensOrder;
                        setLensOrder(lensOrderString);
                    }

                    let lamp = selectedFeature.attributes.lampe as string;
                    if (lamp) {
                        lamp = lamp.split(" (")[0];
                        const incomingCandela = candelaCL.find((candela) => candela.attributes.lampe === lamp);
                        if (incomingCandela) {
                            setSelectedCandela(incomingCandela);
                        } else {
                            setSelectedCandela(null);
                        }

                        try {
                            const intensities = selectedFeature.attributes.lampe.replace(")", "").split(" (");
                            if (intensities.length > 1) {
                                const percentages = intensities[1].split("/");
                                if (percentages.length === 4) {
                                    setTimeout(() => {
                                        setPercentageWhite(Number.parseInt(percentages[0].replace("%", "")));
                                        setPercentageRed(Number.parseInt(percentages[1].replace("%", "")));
                                        setPercentageGreen(Number.parseInt(percentages[2].replace("%", "")));
                                        setPercentageYellow(Number.parseInt(percentages[3].replace("%", "")));
                                    }, 50);
                                }
                            }
                        } catch (e) {
                            console.error(e);
                        }
                    } else {
                        setSelectedCandela(null);
                    }
                    const character = selectedFeature.attributes.karakter;
                    const characterTime = selectedFeature.attributes.periode;
                    if (character) {
                        const tempRelevantCharacters = characterCL.filter((c) => c.attributes.karakter === character);
                        if (tempRelevantCharacters.length > 0) {
                            if (characterTime) {
                                const incomingCharacterTime = tempRelevantCharacters.find(
                                    (c) => c.attributes.periode_kode === characterTime
                                );
                                if (incomingCharacterTime) {
                                    setSelectedCharacter(incomingCharacterTime);
                                } else {
                                    setSelectedCharacter(tempRelevantCharacters[0]);
                                }
                            } else {
                                setSelectedCharacter(tempRelevantCharacters[0]);
                            }
                        }
                    } else {
                        setSelectedCharacter(null);
                    }

                    filterCharacterCL();

                    const incomingBackgroundlight = GetCodedValueAndAlias(
                        selectedFeature.layer,
                        "bakgrunnsbelysning",
                        selectedFeature.attributes.bakgrunnsbelysning
                    )[1]?.toString() as BackgroundLight;
                    setBackgroundLight(incomingBackgroundlight);
                } else {
                    setLightColor(null);
                    setLockedInColor(null);
                    setSelectedCandela(null);
                    setSelectedCharacter(null);
                    setBackgroundLight(BackgroundLight.NONE);
                    setPassthroughMaterial(PassThroughMaterial.NONE);

                    filterCharacterCL();
                }
                isLoadingFromFeature.current = false;
            }, 200);
        } else {
            resetAllValues(true);
            setPercentageWhite(100);
            setPercentageRed(100);
            setPercentageGreen(100);
            setPercentageYellow(100);
        }
    };

    const addLabelToFeatures = () => {
        const mapview = getMapView();
        if (!mapview) {
            return;
        }

        // The light calculator needs karakter and periode even if the user has labels turned off.
        applyLabelsToNFSLayers(resolveLabelsToApply(getMapSettings(), { lightCalcOpen: true }));
    };

    const removeLabelFromFeatures = () => {
        const mapview = getMapView();
        if (!mapview) {
            return;
        }
        mapview.map?.allLayers.forEach((layer) => {
            if (layer.type === "feature") {
                if ((layer as FeatureLayer).fields?.find((field) => field.name === "karakter")) {
                    (layer as FeatureLayer).labelingInfo = [];
                }
            }
        });
        // Restore whatever the user's own label settings say - which may well be "no labels".
        const labels = resolveLabelsToApply(getMapSettings(), { lightCalcOpen: false });
        if (labels.length > 0) {
            applyLabelsToNFSLayers(labels);
        } else {
            removeLabelsFromNFSLayers();
        }
    };

    const shouldUseCandelaForLED = () => {
        if (selectedCandela?.attributes.type === LightType.LED_1) {
            return true;
        }
        return false;
    };

    const setListOfCandela = () => {
        const candelaTable = getFeatureTable("Candela_CL");
        if (!candelaTable) {
            setTimeout(() => {
                setListOfCandela();
            }, 1000);
            return;
        }
        candelaTable.queryFeatures().then((result) => {
            setCandelaCL(result.features);
        });
    };

    const setListOfCharacter = () => {
        const characterTable = getFeatureTable("Karakter_CL");
        if (!characterTable) {
            setTimeout(() => {
                setListOfCharacter();
            }, 1000);
            return;
        }
        characterTable.queryFeatures().then((result) => {
            setCharacterCL(result.features);
        });
    };

    const selectCandela = (lampName: string | null) => {
        setHasUnsavedChangesTrue();
        if (lampName === null) {
            setSelectedCandela(null);
        }
        const selected = relevantCandelaCLs.find((candela) => candela.attributes.lampe === lampName);
        if (selected) {
            setSelectedCandela(selected);
        }
    };

    const selectCharacter = (character: string | null) => {
        setHasUnsavedChangesTrue();
        if (character === null) {
            setSelectedCharacter(null);
        }

        const selected = relevantCharacterCLs.find((c) => {
            return c.attributes.kode === character;
        });

        if (selected) {
            setSelectedCharacter(selected);
        }
    };

    const selectCharacterTime = (time: string | null) => {
        setHasUnsavedChangesTrue();
        const selected = relevantCharacterTimes.find((c) => {
            return c.attributes.periode === time;
        });

        if (selected) {
            setSelectedCharacter(selected);
        }
    };

    const setIntensityFromLensOrder = useCallback(
        (candela: Graphic) => {
            let intensity = candela.attributes.lysstyrke;
            switch (lensOrder) {
                case LensOrder.FIRST:
                    intensity = candela.attributes.ord_glass_1;
                    break;
                case LensOrder.SECOND:
                    intensity = candela.attributes.ord_glass_2;
                    break;
                case LensOrder.THIRD:
                    intensity = candela.attributes.ord_glass_3;
                    break;
                case LensOrder.FOURTH:
                    intensity = candela.attributes.ord_glass_4;
                    break;
                case LensOrder.FIFTH:
                    intensity = candela.attributes.ord_glass_5;
                    break;
                case LensOrder.SIXTH:
                    intensity = candela.attributes.ord_glass_6;
                    break;
            }
            setIntensityIncandescent(intensity);
        },
        [lensOrder]
    );

    const loadLightValues = useCallback(
        (resetLightValues: boolean) => {
            if (selectedCandela) {
                if (selectedCandela.attributes.type === LightType.LED && resetLightValues) {
                    setIntensityWhite(
                        selectedCandela.attributes.lysstyrke_led_hvit ?? selectedCandela.attributes.lysstyrke
                    );
                    setIntensityRed(
                        selectedCandela.attributes.lysstyrke_led_rod ?? selectedCandela.attributes.lysstyrke
                    );
                    setIntensityGreen(
                        selectedCandela.attributes.lysstyrke_led_gronn ?? selectedCandela.attributes.lysstyrke
                    );
                    setIntensityYellow(
                        selectedCandela.attributes.lysstyrke_led_gul ?? selectedCandela.attributes.lysstyrke
                    );
                    setIntensityIncandescent(0);
                } else if (selectedCandela.attributes.type === LightType.Incandescent && resetLightValues) {
                    setIntensityFromLensOrder(selectedCandela);
                    setIntensityWhite(0);
                    setIntensityRed(0);
                    setIntensityGreen(0);
                    setIntensityYellow(0);
                } else if (selectedCandela.attributes.type === LightType.LED_1) {
                    const tau = getShortestActiveTime(selectedCharacter?.attributes.periode);

                    if (tau) {
                        /* white ***********************************/
                        let iWhite = selectedCandela.attributes.lysstyrke_led_hvit;
                        if (tau > 0) {
                            iWhite = omvFixIntensity(iWhite, tau);
                        }
                        setWhiteMaxCandela(iWhite.toFixed(2));
                        let iWhiteMin = beregnMin(iWhite);
                        iWhiteMin = Math.ceil(iWhiteMin);
                        setWhiteMinCandela(iWhiteMin);
                        const { dmin, dmaks } = findRange(iWhiteMin, iWhite, passthroughMaterial, backgroundLight, tau);
                        setRangeWhite(`${dmin.toFixed(2)}Nm - ${dmaks.toFixed(2)}Nm`);

                        /* red ***********************************/
                        let iRed = selectedCandela.attributes.lysstyrke_led_rod;
                        if (tau > 0) {
                            iRed = omvFixIntensity(iRed, tau);
                        }
                        setRedMaxCandela(iRed.toFixed(2));
                        let iRedMin = beregnMin(iRed);
                        iRedMin = Math.ceil(iRedMin);
                        setRedMinCandela(iRedMin);
                        const redRange = findRange(iRedMin, iRed, passthroughMaterial, backgroundLight, tau);
                        setRangeRed(`${redRange.dmin.toFixed(2)}Nm - ${redRange.dmaks.toFixed(2)}Nm`);

                        /* green ***********************************/
                        let iGreen = selectedCandela.attributes.lysstyrke_led_gronn;
                        if (tau > 0) {
                            iGreen = omvFixIntensity(iGreen, tau);
                        }
                        setGreenMaxCandela(iGreen.toFixed(2));
                        let iGreenMin = beregnMin(iGreen);
                        iGreenMin = Math.ceil(iGreenMin);
                        setGreenMinCandela(iGreenMin);
                        const greenRange = findRange(iGreenMin, iGreen, passthroughMaterial, backgroundLight, tau);
                        setRangeGreen(`${greenRange.dmin.toFixed(2)}Nm - ${greenRange.dmaks.toFixed(2)}Nm`);

                        /* yellow ***********************************/
                        let iYellow = selectedCandela.attributes.lysstyrke_led_gul;
                        if (tau > 0) {
                            iYellow = omvFixIntensity(iYellow, tau);
                        }
                        setYellowMaxCandela(iYellow.toFixed(2));
                        let iYellowMin = beregnMin(iYellow);
                        iYellowMin = Math.ceil(iYellowMin);
                        setYellowMinCandela(iYellowMin);
                        const yellowRange = findRange(iYellowMin, iYellow, passthroughMaterial, backgroundLight, tau);
                        setRangeYellow(`${yellowRange.dmin.toFixed(2)}Nm - ${yellowRange.dmaks.toFixed(2)}Nm`);

                        setIntensityIncandescent(0);
                    }
                }
            }
        },
        [
            selectedCandela,
            setIntensityFromLensOrder,
            selectedCharacter?.attributes.periode,
            passthroughMaterial,
            backgroundLight,
        ]
    );

    const canSaveLightValues = () => {
        let result = true;
        if (!selectedFeature || !featureHasLight(selectedFeature)) {
            result = false;
        }

        if (!selectedCandela || !selectedCharacter) {
            result = false;
        }

        if (!canIEditVersion) {
            result = false;
        }

        if (selectedCandela?.attributes.type === LightType.LED_1) {
            if (!desiredDistance || desiredDistance <= 0) {
                result = false;
            }
        }

        if (!result && hasUnsavedChanges && rightPanelActiveTab === RightPanelTabs.LIGHT_CALCULATOR) {
            dispatch(setHasUnsavedChanges(false));
        }

        return result;
    };

    const saveLightValues = () => {
        if (!selectedFeature || !selectedCandela || !selectedCharacter || !featureHasLight(selectedFeature)) {
            console.error("Cannot save light values, missing required data.");
            return;
        }

        if (!canSaveLightValues()) {
            console.error("Cannot save light values, validation failed.");
            return;
        }
        const backgroundLightCode = getDomainCodeFromValue(
            selectedFeature.layer as FeatureLayer,
            "bakgrunnsbelysning",
            backgroundLight
        );
        if (backgroundLightCode === backgroundLight) {
            return;
        }

        setIsSaving(true);

        const copyFeature = selectedFeature.clone();
        copyFeature.attributes = {};
        copyFeature.attributes.objectid = selectedFeature.attributes.objectid;
        copyFeature.attributes.globalid = selectedFeature.attributes.globalid;

        if (selectedCandela.attributes.type === LightType.LED) {
            let lampString = selectedCandela.attributes.lampe;
            lampString += " (";
            if (
                colorsToShow.includes(LightCalculationLightColors.WHITE) ||
                lightColor === LightCalculationLightColors.WHITE
            ) {
                lampString += `${percentageWhite}%/`;
            } else {
                lampString += "0%/";
            }
            if (
                colorsToShow.includes(LightCalculationLightColors.RED) ||
                lightColor === LightCalculationLightColors.RED
            ) {
                lampString += `${percentageRed}%/`;
            } else {
                lampString += "0%/";
            }
            if (
                colorsToShow.includes(LightCalculationLightColors.GREEN) ||
                lightColor === LightCalculationLightColors.GREEN
            ) {
                lampString += `${percentageGreen}%/`;
            } else {
                lampString += "0%/";
            }
            if (
                colorsToShow.includes(LightCalculationLightColors.YELLOW) ||
                lightColor === LightCalculationLightColors.YELLOW
            ) {
                lampString += `${percentageYellow}%)`;
            } else {
                lampString += "0%)";
            }

            copyFeature.attributes.lampe = lampString;
        } else {
            copyFeature.attributes.lampe = selectedCandela.attributes.lampe;
        }

        copyFeature.attributes.karakter = selectedCharacter.attributes.karakter;
        copyFeature.attributes.periode = selectedCharacter.attributes.periode_kode;
        copyFeature.attributes.lys_karakter = selectedCharacter.attributes.lys_karakter;
        copyFeature.attributes.lys_farge = selectedCharacter.attributes.lys_farge;
        copyFeature.attributes.bakgrunnsbelysning = backgroundLightCode;

        copyFeature.attributes.lys_gjennomgang = getDomainCodeFromValue(
            selectedFeature.layer as FeatureLayer,
            "lys_gjennomgang",
            passthroughMaterial
        );

        copyFeature.attributes.lysstyrke_hvit = null;
        copyFeature.attributes.rekkevidde_hvit = null;
        copyFeature.attributes.lysstyrke_rod = null;
        copyFeature.attributes.rekkevidde_rod = null;
        copyFeature.attributes.lysstyrke_gronn = null;
        copyFeature.attributes.rekkevidde_gronn = null;
        copyFeature.attributes.lysstyrke_gul = null;
        copyFeature.attributes.rekkevidde_gul = null;
        copyFeature.attributes.linse_orden = null;

        const statusField = AppConfig.Layers.find((layerConfig) => layerConfig.Name === selectedFeature.layer?.id)
            ?.StatusField as string;
        if (statusField === undefined || statusField === null) return;
        copyFeature.attributes[statusField] = getNewStatus(
            selectedFeature.attributes[statusField],
            NFSStatus.PROJECTED_EXISTING
        );

        if (colorsToShow.length > 0) {
            if (colorsToShow.includes(LightCalculationLightColors.WHITE)) {
                if (selectedCandela?.attributes.type === LightType.LED_1) {
                    copyFeature.attributes.lysstyrke_hvit = resultCandela.effectiveIntensity;
                    copyFeature.attributes.rekkevidde_hvit = desiredDistance;
                } else {
                    copyFeature.attributes.lysstyrke_hvit = calculationResults?.effectiveIntensityWhite;
                    copyFeature.attributes.rekkevidde_hvit = calculationResults?.rangeWhite?.replace(" Nm", "");
                }
            }

            if (colorsToShow.includes(LightCalculationLightColors.RED)) {
                if (selectedCandela?.attributes.type === LightType.LED_1) {
                    copyFeature.attributes.lysstyrke_rod = resultCandela.effectiveIntensity;
                    copyFeature.attributes.rekkevidde_rod = desiredDistance;
                } else {
                    copyFeature.attributes.lysstyrke_rod = calculationResults?.effectiveIntensityRed;
                    copyFeature.attributes.rekkevidde_rod = calculationResults?.rangeRed?.replace(" Nm", "");
                }
            }

            if (colorsToShow.includes(LightCalculationLightColors.GREEN)) {
                if (selectedCandela?.attributes.type === LightType.LED_1) {
                    copyFeature.attributes.lysstyrke_gronn = resultCandela.effectiveIntensity;
                    copyFeature.attributes.rekkevidde_gronn = desiredDistance;
                } else {
                    copyFeature.attributes.lysstyrke_gronn = calculationResults?.effectiveIntensityGreen;
                    copyFeature.attributes.rekkevidde_gronn = calculationResults?.rangeGreen?.replace(" Nm", "");
                }
            }

            if (colorsToShow.includes(LightCalculationLightColors.YELLOW)) {
                if (selectedCandela?.attributes.type === LightType.LED_1) {
                    copyFeature.attributes.lysstyrke_gul = resultCandela.effectiveIntensity;
                    copyFeature.attributes.rekkevidde_gul = desiredDistance;
                } else {
                    copyFeature.attributes.lysstyrke_gul = calculationResults?.effectiveIntensityYellow;
                    copyFeature.attributes.rekkevidde_gul = calculationResults?.rangeYellow?.replace(" Nm", "");
                }
            }
        } else {
            switch (lightColor) {
                case LightCalculationLightColors.WHITE:
                    if (selectedCandela?.attributes.type === LightType.LED_1) {
                        copyFeature.attributes.lysstyrke_hvit = resultCandela.effectiveIntensity;
                        copyFeature.attributes.rekkevidde_hvit = desiredDistance;
                    } else {
                        copyFeature.attributes.lysstyrke_hvit = calculationResults?.effectiveIntensityWhite;
                        copyFeature.attributes.rekkevidde_hvit = calculationResults?.rangeWhite?.replace(" Nm", "");
                    }
                    break;
                case LightCalculationLightColors.RED:
                    if (selectedCandela?.attributes.type === LightType.LED_1) {
                        copyFeature.attributes.lysstyrke_rod = resultCandela.effectiveIntensity;
                        copyFeature.attributes.rekkevidde_rod = desiredDistance;
                    } else {
                        copyFeature.attributes.lysstyrke_rod = calculationResults?.effectiveIntensityRed;
                        copyFeature.attributes.rekkevidde_rod = calculationResults?.rangeRed?.replace(" Nm", "");
                    }
                    break;
                case LightCalculationLightColors.GREEN:
                    if (selectedCandela?.attributes.type === LightType.LED_1) {
                        copyFeature.attributes.lysstyrke_gronn = resultCandela.effectiveIntensity;
                        copyFeature.attributes.rekkevidde_gronn = desiredDistance;
                    } else {
                        copyFeature.attributes.lysstyrke_gronn = calculationResults?.effectiveIntensityGreen;
                        copyFeature.attributes.rekkevidde_gronn = calculationResults?.rangeGreen?.replace(" Nm", "");
                    }
                    break;
                case LightCalculationLightColors.YELLOW:
                    if (selectedCandela?.attributes.type === LightType.LED_1) {
                        copyFeature.attributes.lysstyrke_gul = resultCandela.effectiveIntensity;
                        copyFeature.attributes.rekkevidde_gul = desiredDistance;
                    } else {
                        copyFeature.attributes.lysstyrke_gul = calculationResults?.effectiveIntensityYellow;
                        copyFeature.attributes.rekkevidde_gul = calculationResults?.rangeYellow?.replace(" Nm", "");
                    }
                    break;
            }
        }

        if (selectedCandela?.attributes.type === LightType.Incandescent) {
            copyFeature.attributes.linse_orden = lensOrderStringToNumber(lensOrder as LensOrder);
        }

        appContext?.featureEditsInFlight.begin();
        applyEdits(selectedFeature.layer as FeatureLayer, {
            updateFeatures: [copyFeature],
        })
            .then((result) => {
                if (result.updateFeatureResults[0].error) {
                    findAndSelectFeature(selectedFeature.layer as FeatureLayer, selectedFeature, (graphic) => {
                        appContext.selectedFeature.set(graphic);
                    });
                } else {
                    dispatch(setHasUnsavedChanges(false));
                    for (const key of Object.keys(selectedFeature.attributes)) {
                        if (!Object.keys(copyFeature.attributes).includes(key)) {
                            copyFeature.attributes[key] = selectedFeature.attributes[key];
                        }
                    }
                    const updateObject = selectedFeature?.clone();
                    if (!updateObject) return false;
                    updateObject.attributes = copyFeature.attributes;
                    updateObject.layer = selectedFeature.layer;
                    updateObject.geometry = selectedFeature.geometry;
                    appContext?.selectedFeature.set(updateObject);
                    if (
                        appContext?.shownFeature.value?.attributes["objectid"] === updateObject.attributes["objectid"]
                    ) {
                        appContext?.shownFeature.set(updateObject);
                    }

                    if (updateObject.attributes["nfs_status"] == NFSStatus.PROJECTED) {
                        if (updateObject.attributes["gis_obj_id"]) {
                            const layer = updateObject.layer as FeatureLayer;
                            updateFDVObjectNew(
                                {
                                    layerId: layer.layerId,
                                    globalId: updateObject.attributes.globalid,
                                    gdbVersion: layer.gdbVersion,
                                },
                                updateObject
                            )
                                .then((result) => {
                                    if (!result.success) {
                                        console.error("Sync of FDV object after light calc failed.");
                                    }
                                })
                                .catch((err) => {
                                    console.error(err);
                                    console.error("Sync of FDV object after light calc failed.");
                                });
                        } else {
                            toast.warning("Lysberegning lagret, objektet har ikke blitt knyttet til FDV ennå.");
                        }
                    }

                    if (selectedWorkOrderIds.length === 0) {
                        toast.warning("Lysberegning lagret, ingen tilknyttede FDV arbeidsordre valgt for oppdatering.");
                    } else {
                        const featureLayer = selectedFeature.layer as FeatureLayer;
                        updateFDVWorkOrdersWithLightParams(
                            selectedWorkOrderIds,
                            featureLayer.layerId as number,
                            updateObject.attributes.globalid,
                            featureLayer.gdbVersion
                        )
                            .then((result) => {
                                if (result) {
                                    toast.success("FDV arbeidsordre oppdatert med lysberegning.");
                                } else {
                                    toast.error("Oppdatering av FDV arbeidsordre med lysberegning feilet.");
                                }
                            })
                            .catch((err) => {
                                console.error(err);
                                toast.error("Det oppsto en feil ved oppdatering av FDV arbeidsordre med lysberegning.");
                            });
                    }
                }
            })
            .catch(() => {
                findAndSelectFeature(selectedFeature.layer as FeatureLayer, selectedFeature, (graphic) => {
                    appContext.selectedFeature.set(graphic);
                });
            })
            .finally(() => {
                appContext?.featureEditsInFlight.end();
                setIsSaving(false);
            });
    };

    const handleDesiredDistanceChange = (value: string) => {
        setHasUnsavedChangesTrue();
        setDesiredDistanceDynamicInput(value);
        if (activeUnit === "Nm") {
            setDesiredDistance(Number.parseFloat(value));
        } else {
            setDesiredDistance(Number.parseFloat(value) / metersPerNauticalMile);
        }
    };

    const updateLightRangeInMap = () => {
        if (!selectedFeature || !showRangeInMap || !calculationResults) {
            return;
        }

        let lightCenter = selectedFeature.geometry as Point | undefined;
        if (selectedFeature.geometry && selectedFeature.geometry?.type !== "point") {
            lightCenter = selectedFeature.geometry.extent?.center;
        }
        if (!lightCenter) return;

        if (selectedCandela?.attributes.type !== LightType.LED_1) {
            clearLightInMap();

            if (
                colorsToShow.includes(LightCalculationLightColors.WHITE) ||
                lightColor == LightCalculationLightColors.WHITE
            ) {
                const radiusWhite = calculationResults?.rangeWhite
                    ? Number.parseFloat(calculationResults.rangeWhite.replace("Nm", ""))
                    : 0;
                renderLightInMap(lightCenter, "white", radiusWhite);
            }
            if (
                colorsToShow.includes(LightCalculationLightColors.RED) ||
                lightColor == LightCalculationLightColors.RED
            ) {
                const radiusRed = calculationResults?.rangeRed
                    ? Number.parseFloat(calculationResults.rangeRed.replace("Nm", ""))
                    : 0;
                renderLightInMap(lightCenter, "red", radiusRed);
            }

            if (
                colorsToShow.includes(LightCalculationLightColors.GREEN) ||
                lightColor == LightCalculationLightColors.GREEN
            ) {
                const radiusGreen = calculationResults?.rangeGreen
                    ? Number.parseFloat(calculationResults.rangeGreen.replace("Nm", ""))
                    : 0;
                renderLightInMap(lightCenter, "green", radiusGreen);
            }

            if (
                colorsToShow.includes(LightCalculationLightColors.YELLOW) ||
                lightColor == LightCalculationLightColors.YELLOW
            ) {
                const radiusYellow = calculationResults?.rangeYellow
                    ? Number.parseFloat(calculationResults.rangeYellow.replace("Nm", ""))
                    : 0;
                renderLightInMap(lightCenter, "yellow", radiusYellow);
            }
        }
    };

    const setHasUnsavedChangesTrue = () => {
        if (!hasUnsavedChanges && selectedFeature && featureHasLight(selectedFeature)) {
            dispatch(setHasUnsavedChanges(true));
        }
        if (hasUnsavedChanges && !selectedFeature) {
            dispatch(setHasUnsavedChanges(false));
        }
    };

    useEffect(() => {
        updateLightRangeInMap();

        // Get the map view and watch for scale changes
        const mapView = getMapView();
        let scaleWatcher: ResourceHandle | null = null;

        if (mapView) {
            scaleWatcher = reactiveUtils.watch(
                () => mapView.scale,
                () => {
                    updateLightRangeInMap();
                }
            );
        }

        // Clean up the watcher when component unmounts or dependencies change
        return () => {
            if (scaleWatcher) {
                scaleWatcher.remove();
            }
        };
    }, [calculationResults, showRangeInMap]);

    useEffect(() => {
        if (!selectedFeature || !showRangeInMap || !resultCandela || !featureHasLight(selectedFeature)) {
            return;
        }

        let lightCenter = selectedFeature.geometry as Point | undefined;
        if (selectedFeature.geometry?.type !== "point") {
            lightCenter = selectedFeature.geometry?.extent?.center;
        }

        if (!lightCenter) return;

        if (selectedCandela?.attributes.type === LightType.LED_1) {
            const radius = desiredDistance;
            clearLightInMap();
            renderLightInMap(lightCenter, "led-160", radius, resultCandela.message !== "");
        }
    }, [resultCandela, showRangeInMap]);

    useEffect(() => {
        if (!showRangeInMap) {
            clearLightInMap();
        }
    }, [showRangeInMap]);

    useEffect(() => {
        if (activeUnit === "Nm") {
            setDesiredDistanceDynamicInput(desiredDistance.toFixed(2));
        } else {
            setDesiredDistanceDynamicInput((desiredDistance * metersPerNauticalMile).toFixed(2));
        }
    }, [activeUnit]);

    useEffect(() => {
        if (selectedCandela) {
            setIntensityFromLensOrder(selectedCandela);
        }
    }, [lensOrder]);

    useEffect(() => {
        loadLightValues(false);
    }, [selectedCharacter, desiredDistance, backgroundLight, lightColor, passthroughMaterial]);

    // Filter relevant characters based on selected light color
    useEffect(() => {
        filterCharacterCL();
    }, [lightColor]);

    useEffect(() => {
        if (!selectedCandela) {
            return;
        }
        resetAllValues(false);
        loadLightValues(true);
    }, [selectedCandela]);

    // Finds the relevant character times based on selected character
    // and sets the shortest active time
    useEffect(() => {
        if (selectedCharacter === null) {
            setRelevantCharacterTimes([]);
            setShortestActiveTime("");
        }
        if (selectedCharacter) {
            const relCharacterTimes = characterCL.filter((character) => {
                return character.attributes.kode === selectedCharacter.attributes.kode;
            });
            setRelevantCharacterTimes(relCharacterTimes);
            const shortestTime = getShortestActiveTime(selectedCharacter.attributes.periode);
            setShortestActiveTime(shortestTime.toString());
            const colorCode = selectedCharacter.attributes.lys_farge;
            const mapView = getMapView();
            const lightLayer = mapView?.map?.findLayerById("Lys") as FeatureLayer;
            const colorValue = GetCodedValueAndAlias(lightLayer, "lys_farge", colorCode)[1].toString();
            if (colorValue === "0" || colorCode === 0) {
                const characterColors = getCharacterColors(selectedCharacter.attributes.kode);
                setColorsToShow(characterColors);
                setLightColor(null);
            } else {
                setLightColor(colorValue as LightCalculationLightColors);
                setColorsToShow([]);
            }
            loadLightValues(false);
        }
    }, [selectedCharacter]);

    useEffect(() => {
        const filteredCandela = candelaCL.filter((candela) => {
            let shouldShow = candela.attributes.aktiv === 1;
            if (selectedFeature && featureHasLight(selectedFeature)) {
                const featureKlasse = selectedFeature.attributes.klasse;
                const candelaValidKlasse = candela.attributes.tilordnet_klasse as string;
                if (featureKlasse && candelaValidKlasse) {
                    const validClasses = candelaValidKlasse.replaceAll(" ", "").split(",");
                    if (!validClasses.includes(featureKlasse.toString())) {
                        shouldShow = false;
                    }
                }
                const featureColor = selectedFeature.attributes.lys_farge;
                if (
                    (featureColor === SeamarkColors.White ||
                        featureColor === SeamarkColors.Red ||
                        featureColor === SeamarkColors.Yellow ||
                        featureColor === SeamarkColors.Green) &&
                    (candela.attributes.type === LightType.LED || candela.attributes.type === LightType.LED_1)
                ) {
                    switch (featureColor) {
                        case SeamarkColors.White:
                            if (candela.attributes.lysstyrke_led_hvit === null) {
                                shouldShow = false;
                            }
                            break;
                        case SeamarkColors.Red:
                            if (candela.attributes.lysstyrke_led_rod === null) {
                                shouldShow = false;
                            }
                            break;
                        case SeamarkColors.Green:
                            if (candela.attributes.lysstyrke_led_gronn === null) {
                                shouldShow = false;
                            }
                            break;
                        case SeamarkColors.Yellow:
                            if (candela.attributes.lysstyrke_led_gul === null) {
                                shouldShow = false;
                            }
                            break;
                    }
                }
            }

            return shouldShow;
        });
        const sortedCandela = filteredCandela.toSorted((a, b) => {
            // Handle null values - put them at the end
            if (a.attributes.tilordnet_klasse_prioritet === null) {
                return 1; // Move 'a' to the end if it has null priority
            }
            if (b.attributes.tilordnet_klasse_prioritet === null) {
                return -1; // Move 'b' to the end if it has null priority
            }
            // Normal comparison for non-null values
            if (a.attributes.tilordnet_klasse_prioritet < b.attributes.tilordnet_klasse_prioritet) {
                return -1;
            }
            if (a.attributes.tilordnet_klasse_prioritet > b.attributes.tilordnet_klasse_prioritet) {
                return 1;
            }
            return 0;
        });
        setRelevantCandelaCLs(sortedCandela);
    }, [candelaCL, selectedFeature]);

    // Filters characters to only show unique characters
    useEffect(() => {
        if (relevantCharacterCLs.length === 0) {
            filterCharacterCL();
        }
    }, [characterCL]);

    useEffect(() => {
        if (selectedCharacter && selectedCandela) {
            const shortestActiveTimeNumber = Number.parseFloat(shortestActiveTime);
            if (selectedCandela?.attributes.type === LightType.LED) {
                const result = lightCalculationLED(
                    shortestActiveTimeNumber,
                    passthroughMaterial,
                    backgroundLight,
                    intensityWhite,
                    percentageWhite,
                    intensityRed,
                    percentageRed,
                    intensityGreen,
                    percentageGreen,
                    intensityYellow,
                    percentageYellow
                );
                setCalculationResults(result);
            } else if (selectedCandela?.attributes.type === LightType.Incandescent) {
                const result = lightCalculationIncandescent(
                    intensityIncandescent,
                    shortestActiveTimeNumber,
                    passthroughMaterial,
                    selectedCharacter.attributes.lys_farge,
                    backgroundLight
                );
                setCalculationResults(result);
            } else if (selectedCandela.attributes.type === LightType.LED_1) {
                loadLightValues(false);
                const colorCode = selectedCharacter.attributes.lys_farge;
                const result = lightCalculationCandela(
                    desiredDistance,
                    passthroughMaterial,
                    backgroundLight,
                    shortestActiveTimeNumber,
                    colorCode,
                    whiteMinCandela,
                    whiteMaxCandela,
                    redMinCandela,
                    redMaxCandela,
                    greenMinCandela,
                    greenMaxCandela,
                    yellowMinCandela,
                    yellowMaxCandela
                );
                setResultCandela(result);
            }
        }
    }, [
        selectedCharacter,
        shortestActiveTime,
        passthroughMaterial,
        backgroundLight,
        intensityWhite,
        percentageWhite,
        intensityRed,
        percentageRed,
        intensityGreen,
        percentageGreen,
        intensityIncandescent,
        desiredDistance,
        whiteMinCandela,
        whiteMaxCandela,
        redMinCandela,
        redMaxCandela,
        greenMinCandela,
        greenMaxCandela,
        selectedCandela,
        loadLightValues,
        yellowMinCandela,
        yellowMaxCandela,
        intensityYellow,
        percentageYellow,
    ]);

    const applyHighlight = async () => {
        clearHighlightedFeatures();
        if (selectedFeature) {
            const mapview = getMapView();
            if (!mapview) return;
            const highlightHandle = await highlightFeature(selectedFeature);
            if (!highlightHandle) return;
            highlightHandlesRef.current.push(highlightHandle);
        }
    };

    const clearHighlightedFeatures = () => {
        highlightHandlesRef.current.forEach((handle) => {
            handle.remove();
        });
        highlightHandlesRef.current = [];
    };

    useEffect(() => {
        if (lightCalculatorOpen.open && lightCalculatorOpen.loadFeatureValues) {
            resetAndLoadValuesFromSelectedFeature(true);
        }
        const currentGlobalId = selectedFeature?.attributes?.globalid ?? null;
        if (previousSelectedFeatureGlobalIdRef.current !== currentGlobalId) {
            setSelectedWorkOrderIds([]);
            previousSelectedFeatureGlobalIdRef.current = currentGlobalId;
        }

        applyHighlight();
        if (selectedFeature) {
            layerConfig.current = AppConfig.Layers.find(
                (layerConfig) => layerConfig.Name === selectedFeature?.layer?.id
            );

            symbolUtils.getDisplayedSymbol(selectedFeature).then((symbol) => {
                if (symbol) {
                    symbolUtils.renderPreviewHTML(symbol, { size: 12 }).then((symbolElement) => {
                        if (!symbolElement) return;
                        setSymbolHtmlElement(symbolElement);
                    });
                } else {
                    setSymbolHtmlElement(null);
                }
            });
            getRelatedFeatures([selectedFeature], "FlytendeMerke").then((relatedFeatures) => {
                if (relatedFeatures.length > 0) {
                    setSelectedFeatureRelatedMerke(relatedFeatures[0]);
                } else {
                    setSelectedFeatureRelatedMerke(null);
                }
            });
        } else {
            setSymbolHtmlElement(null);
            layerConfig.current = undefined;
            setSelectedFeatureRelatedMerke(null);
        }
    }, [selectedFeature]);

    useEffect(() => {
        const user = userContext?.user.value;
        canEditVersion(activeVersion, versionsSharedWithMe, user).then((result) => {
            setCanIEditVersion(result);
        });
    }, [activeVersion, versionsSharedWithMe]);

    useEffect(() => {
        const shortestActiveTimeNumber = Number.parseFloat(shortestActiveTime);
        setListOfCandela();
        setListOfCharacter();
        const result = lightCalculationLED(
            shortestActiveTimeNumber,
            passthroughMaterial,
            backgroundLight,
            intensityWhite,
            percentageWhite,
            intensityRed,
            percentageRed,
            intensityGreen,
            percentageGreen,
            intensityYellow,
            percentageYellow
        );
        setCalculationResults(result);

        return () => {
            clearHighlightedFeatures();
        };
    }, []);

    useEffect(() => {
        if (lightCalculatorOpen.open) {
            if (lightCalculatorOpen.loadFeatureValues) {
                resetAndLoadValuesFromSelectedFeature(true);
            }
            addLabelToFeatures();
        } else {
            setShowRangeInMap(false);
            removeLabelFromFeatures();
        }
    }, [lightCalculatorOpen]);

    useEffect(() => {
        if (rightPanelActiveTab !== RightPanelTabs.LIGHT_CALCULATOR) {
            resetAllValues(true);
            dispatch(setHasUnsavedChanges(false));
            clearHighlightedFeatures();
        }
    }, [rightPanelActiveTab]);

    const closePanel = () => {
        const result = dispatch(changeRightPanelTab(RightPanelTabs.UNDEFINED));
        if (!result) {
            // User cancelled, don't unselect
            return;
        }
        appContext?.selectedFeature.set(null);
        resetAllValues(true);
        dispatch(setLightCalculatorOpen({ open: false, loadFeatureValues: false }));
    };

    const goToEditing = () => {
        const result = dispatch(changeRightPanelTab(RightPanelTabs.EDITOR));
        if (!result) {
            // User cancelled, don't unselect
            return;
        }
        dispatch(setLightCalculatorOpen({ open: false, loadFeatureValues: false }));
    };

    return (
        <div className={`light-calculator`}>
            {isSaving && <Loader overlay="element" targetElement=".light-calculator" size={60} />}
            <div className="light-calculator-header">
                {selectedFeature && (
                    <button onClick={goToEditing} className="empty-button flex-row center">
                        <ReturnIcon />
                        Tilbake
                    </button>
                )}
                <span className="flex-row small-gap" style={{ width: "100%" }}>
                    Lysberegning
                </span>
                <button type="button" className="empty-button" onClick={closePanel}>
                    <CloseIcon />
                </button>
            </div>
            {selectedFeature && featureHasLight(selectedFeature) && (
                <FeatureViewerTitle
                    layerConfig={layerConfig}
                    feature={selectedFeature}
                    symbolHtmlElement={symbolHtmlElement}
                    canMove={false}
                />
            )}
            {selectedFeature && featureHasLight(selectedFeature) && (
                <FeatureWorkOrderGroup
                    feature={selectedFeature}
                    isEditing={false}
                    canBeSentToFdv={false}
                    canHaveFdvWorkOrders={true}
                    selectable={true}
                    selectedWorkOrderIds={selectedWorkOrderIds}
                    setSelectedWorkOrderIds={setSelectedWorkOrderIds}
                    workOrderClassFilter={"DIR"}
                />
            )}
            <div className="light-calculator-content">
                <div className="flex-col w-100 small-gap">
                    <RadioButtonGroup
                        options={["Vis i Nm", "Vis i m"]}
                        selectedIndex={activeUnit === "Nm" ? 0 : 1}
                        onChange={(selectedIndex) => {
                            setActiveUnit(selectedIndex === 0 ? "Nm" : "m");
                        }}
                    />
                    {selectedFeature && featureHasLight(selectedFeature) && (
                        <div className="flex-row small-gap align-center space-between">
                            <span>Vis rekkevidde i kartet</span>
                            <Toggle
                                small
                                checked={showRangeInMap}
                                onChange={() => {
                                    setShowRangeInMap(!showRangeInMap);
                                }}
                            />
                        </div>
                    )}
                </div>
                <div className="light-calculator-input-fields">
                    {!shouldUseCandelaForLED() && (
                        <>
                            <div className="flex-row w-100 small-gap">
                                <label className={`input-field-label`}>
                                    Lampe
                                    <NfsDropdown
                                        options={relevantCandelaCLs.map((candela) => candela.attributes.lampe)}
                                        allowEmpty
                                        selectedOption={selectedCandela?.attributes.lampe ?? null}
                                        setSelectedOption={selectCandela}
                                        placeholder="Velg lampe"
                                        searchable
                                    />
                                </label>
                            </div>
                            <div className="flex-row w-100 small-gap">
                                <InputField
                                    inputFieldId="lamp-type"
                                    label="Lampe type"
                                    type="text"
                                    readonly
                                    value={selectedCandela?.attributes.type ?? ""}
                                    onChange={() => {}}
                                />
                                {selectedCandela?.attributes.type === LightType.Incandescent && (
                                    <NfsDropdown
                                        options={[
                                            LensOrder.FIRST,
                                            LensOrder.SECOND,
                                            LensOrder.THIRD,
                                            LensOrder.FOURTH,
                                            LensOrder.FIFTH,
                                            LensOrder.SIXTH,
                                        ]}
                                        allowEmpty
                                        selectedOption={lensOrder}
                                        setSelectedOption={(value) => {
                                            setLensOrder(value as LensOrder);
                                            setHasUnsavedChangesTrue();
                                        }}
                                        placeholder="Velg linse orden"
                                    />
                                )}
                            </div>
                            <div className="flex-row small-gap w-100">
                                <label className={`input-field-label`}>
                                    Fyrliste karakter
                                    <NfsDropdown
                                        options={relevantCharacterCLs
                                            .map((character) => character.attributes.kode)
                                            .sort((a: string, b: string) => {
                                                return a.localeCompare(b, undefined, { numeric: true });
                                            })}
                                        allowEmpty
                                        selectedOption={selectedCharacter?.attributes.kode ?? null}
                                        setSelectedOption={selectCharacter}
                                        placeholder="Velg fyrliste karakter"
                                        searchable
                                    />
                                </label>
                            </div>
                            <div className="flex-row w-100 small-gap">
                                <label className={`input-field-label`}>
                                    Karakter
                                    <NfsDropdown
                                        disabled={selectedCharacter === null}
                                        options={relevantCharacterTimes.map(
                                            (character) => character.attributes.periode
                                        )}
                                        selectedOption={selectedCharacter?.attributes.periode ?? null}
                                        setSelectedOption={selectCharacterTime}
                                        placeholder="Velg karakter"
                                    />
                                </label>
                            </div>
                            <div className="flex-row w-100 small-gap">
                                <label className={`input-field-label`}>
                                    Bakgrunnsbelysning
                                    <NfsDropdown
                                        options={[BackgroundLight.NONE, BackgroundLight.MEDIUM, BackgroundLight.HIGH]}
                                        selectedOption={backgroundLight}
                                        setSelectedOption={(value) => {
                                            setBackgroundLight(value as BackgroundLight);
                                            setHasUnsavedChangesTrue();
                                        }}
                                        placeholder="Velg bakgrunnsbelysning"
                                    />
                                </label>
                                <InputField
                                    inputFieldId="shortest-active-time"
                                    label="Korteste aktive tid"
                                    type="text"
                                    value={shortestActiveTime}
                                    readonly
                                    onChange={() => {}}
                                />
                            </div>
                            <div className="flex-row w-100 small-gap">
                                <label className={`input-field-label`}>
                                    Lysfarge
                                    <NfsDropdown
                                        options={[
                                            LightCalculationLightColors.WHITE,
                                            LightCalculationLightColors.RED,
                                            LightCalculationLightColors.GREEN,
                                            LightCalculationLightColors.YELLOW,
                                        ]}
                                        allowEmpty
                                        readonly
                                        disabled={colorsToShow.length === 0 && lightColor === null}
                                        selectedOption={colorsToShow.length > 0 ? colorsToShow.join(", ") : lightColor}
                                        setSelectedOption={(value) =>
                                            setLightColor(value as LightCalculationLightColors)
                                        }
                                        placeholder="Velg fyrliste karakter"
                                    />
                                </label>
                                <label className={`input-field-label`}>
                                    Lysgjennomgang
                                    <NfsDropdown
                                        options={[
                                            PassThroughMaterial.GLASS,
                                            PassThroughMaterial.PLASTIC,
                                            PassThroughMaterial.NONE,
                                        ]}
                                        selectedOption={passthroughMaterial}
                                        setSelectedOption={(value) => {
                                            setHasUnsavedChangesTrue();
                                            setPassthroughMaterial(value as PassThroughMaterial);
                                        }}
                                        placeholder="Velg lysgjennomgang"
                                    />
                                </label>
                            </div>
                            {selectedCandela?.attributes.type === LightType.Incandescent && (
                                <div className="flex-row w-100 small-gap">
                                    <InputField
                                        inputFieldId="light-intensity-incandescent"
                                        label="Lysstyrke"
                                        type="number"
                                        readonly
                                        value={intensityIncandescent?.toString()}
                                        valueSuffix="cd"
                                        onChange={(value) => {
                                            setIntensityIncandescent(Number.parseFloat(value));
                                            setHasUnsavedChangesTrue();
                                        }}
                                    />
                                </div>
                            )}
                            {(lightColor === LightCalculationLightColors.WHITE ||
                                (lightColor === null && colorsToShow.includes(LightCalculationLightColors.WHITE))) && (
                                <>
                                    {selectedCandela?.attributes.type === LightType.LED && (
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="light-intensity-white"
                                                label="Lysstyrke (Hvit)"
                                                type="number"
                                                valueSuffix="cd"
                                                value={intensityWhite?.toString()}
                                                onChange={(value) => {
                                                    setIntensityWhite(Number.parseFloat(value));
                                                    setHasUnsavedChangesTrue();
                                                }}
                                            />
                                            <InputField
                                                inputFieldId="percentage-used-white"
                                                label="Prosent utnyttelse"
                                                type="number"
                                                min={0}
                                                max={100}
                                                valueSuffix="percent"
                                                value={percentageWhite?.toString()}
                                                onChange={(value) => {
                                                    setPercentageWhite(Number.parseFloat(value));
                                                    setHasUnsavedChangesTrue();
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="flex-col small-gap">
                                        {selectedFeature && featureHasLight(selectedFeature) && (
                                            <div className="flex-row w-100 small-gap">
                                                <InputField
                                                    inputFieldId="light-intensity-white"
                                                    label="Dagens lystyrke hvit"
                                                    type="text"
                                                    valueSuffix="cd"
                                                    value={selectedFeature.attributes.lysstyrke_hvit ?? ""}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                                <InputField
                                                    inputFieldId="light-range-white"
                                                    label="Dagens rekkevidde hvit"
                                                    type="text"
                                                    valueSuffix={activeUnit}
                                                    value={formatRangeNm(
                                                        selectedFeature.attributes.rekkevidde_hvit,
                                                        activeUnit === "m",
                                                        false
                                                    )}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                            </div>
                                        )}
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="effective-light-intensity-white"
                                                label="Effektiv lysstyrke hvit"
                                                type="text"
                                                valueSuffix="cd"
                                                value={calculationResults?.effectiveIntensityWhite ?? ""}
                                                readonly
                                                onChange={() => {}}
                                            />
                                            <InputField
                                                inputFieldId="calculated-range-white"
                                                label="Beregnet rekkevidde hvit"
                                                type="text"
                                                valueSuffix={activeUnit}
                                                value={formatRangeNm(
                                                    calculationResults?.rangeWhite,
                                                    activeUnit === "m",
                                                    false
                                                )}
                                                readonly
                                                onChange={() => {}}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {(lightColor === LightCalculationLightColors.RED ||
                                (lightColor === null && colorsToShow.includes(LightCalculationLightColors.RED))) && (
                                <>
                                    {selectedCandela?.attributes.type === LightType.LED && (
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="light-intensity-red"
                                                label="Lysstyrke (Rød)"
                                                type="number"
                                                valueSuffix="cd"
                                                value={intensityRed?.toString()}
                                                onChange={(value) => {
                                                    setIntensityRed(Number.parseFloat(value));
                                                    setHasUnsavedChangesTrue();
                                                }}
                                            />
                                            <InputField
                                                inputFieldId="percentage-used-red"
                                                label="Prosent utnyttelse"
                                                type="number"
                                                min={0}
                                                max={100}
                                                valueSuffix="percent"
                                                value={percentageRed?.toString()}
                                                onChange={(value) => {
                                                    setPercentageRed(Number.parseFloat(value));
                                                    setHasUnsavedChangesTrue();
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="flex-col small-gap">
                                        {selectedFeature && featureHasLight(selectedFeature) && (
                                            <div className="flex-row w-100 small-gap">
                                                <InputField
                                                    inputFieldId="light-intensity-red"
                                                    label="Dagens lystyrke rød"
                                                    type="text"
                                                    valueSuffix="cd"
                                                    value={selectedFeature.attributes.lysstyrke_rod ?? ""}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                                <InputField
                                                    inputFieldId="light-range-red"
                                                    label="Dagens rekkevidde rød"
                                                    type="text"
                                                    valueSuffix={activeUnit}
                                                    value={formatRangeNm(
                                                        selectedFeature.attributes.rekkevidde_rod,
                                                        activeUnit === "m",
                                                        false
                                                    )}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                            </div>
                                        )}
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="effective-light-intensity-red"
                                                label="Effektiv lysstyrke rød"
                                                type="text"
                                                valueSuffix="cd"
                                                value={calculationResults?.effectiveIntensityRed ?? ""}
                                                readonly
                                                onChange={() => {}}
                                            />
                                            <InputField
                                                inputFieldId="calculated-range-red"
                                                label="Beregnet rekkevidde rød"
                                                type="text"
                                                valueSuffix={activeUnit}
                                                value={formatRangeNm(
                                                    calculationResults?.rangeRed,
                                                    activeUnit === "m",
                                                    false
                                                )}
                                                readonly
                                                onChange={() => {}}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {(lightColor === LightCalculationLightColors.GREEN ||
                                (lightColor === null && colorsToShow.includes(LightCalculationLightColors.GREEN))) && (
                                <>
                                    {selectedCandela?.attributes.type === LightType.LED && (
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="light-intensity-green"
                                                label="Lysstyrke (Grønn)"
                                                type="number"
                                                valueSuffix="cd"
                                                value={intensityGreen?.toString()}
                                                onChange={(value) => {
                                                    setIntensityGreen(Number.parseFloat(value));
                                                    setHasUnsavedChangesTrue();
                                                }}
                                            />
                                            <InputField
                                                inputFieldId="percentage-used-green"
                                                label="Prosent utnyttelse"
                                                type="number"
                                                min={0}
                                                max={100}
                                                valueSuffix="percent"
                                                value={percentageGreen?.toString()}
                                                onChange={(value) => {
                                                    setPercentageGreen(Number.parseFloat(value));
                                                    setHasUnsavedChangesTrue();
                                                }}
                                            />
                                        </div>
                                    )}
                                    <div className="flex-col small-gap">
                                        {selectedFeature && featureHasLight(selectedFeature) && (
                                            <div className="flex-row w-100 small-gap">
                                                <InputField
                                                    inputFieldId="light-intensity-green"
                                                    label="Dagens lystyrke grønn"
                                                    type="text"
                                                    valueSuffix="cd"
                                                    value={selectedFeature.attributes.lysstyrke_gronn ?? ""}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                                <InputField
                                                    inputFieldId="light-range-green"
                                                    label="Dagens rekkevidde grønn"
                                                    type="text"
                                                    valueSuffix={activeUnit}
                                                    value={formatRangeNm(
                                                        selectedFeature.attributes.rekkevidde_gronn,
                                                        activeUnit === "m",
                                                        false
                                                    )}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                            </div>
                                        )}
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="effective-light-intensity-green"
                                                label="Effektiv lysstyrke grønn"
                                                type="text"
                                                valueSuffix="cd"
                                                value={calculationResults?.effectiveIntensityGreen ?? ""}
                                                readonly
                                                onChange={() => {}}
                                            />
                                            <InputField
                                                inputFieldId="calculated-range-green"
                                                label="Beregnet rekkevidde grønn"
                                                type="text"
                                                valueSuffix={activeUnit}
                                                value={formatRangeNm(
                                                    calculationResults?.rangeGreen,
                                                    activeUnit === "m",
                                                    false
                                                )}
                                                readonly
                                                onChange={() => {}}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {selectedCandela?.attributes.type === LightType.Incandescent && (
                                <>
                                    {(lightColor === LightCalculationLightColors.YELLOW ||
                                        (lightColor === null &&
                                            colorsToShow.includes(LightCalculationLightColors.YELLOW))) && (
                                        <>
                                            {/* <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="light-intensity-yellow"
                                                label="Lysstyrke (Gul)"
                                                type="number"
                                                value={""}
                                                onChange={() => { }}
                                            />
                                            <InputField
                                                inputFieldId="percentage-used-yellow"
                                                label="Prosent utnyttelse"
                                                type="number"
                                                min={0}
                                                max={100}
                                                value={""}
                                                onChange={() => { }}
                                            />
                                        </div> */}
                                            <div className="flex-col small-gap">
                                                {selectedFeature && featureHasLight(selectedFeature) && (
                                                    <div className="flex-row w-100 small-gap">
                                                        <InputField
                                                            inputFieldId="light-intensity-yellow"
                                                            label="Dagens lystyrke gul"
                                                            type="text"
                                                            valueSuffix="cd"
                                                            value={selectedFeature.attributes.lysstyrke_gul ?? ""}
                                                            readonly
                                                            className="light-calculator-disabled-input"
                                                            onChange={() => {}}
                                                        />
                                                        <InputField
                                                            inputFieldId="light-range-yellow"
                                                            label="Dagens rekkevidde gul"
                                                            type="text"
                                                            valueSuffix={activeUnit}
                                                            value={formatRangeNm(
                                                                selectedFeature.attributes.rekkevidde_gul,
                                                                activeUnit === "m",
                                                                false
                                                            )}
                                                            readonly
                                                            className="light-calculator-disabled-input"
                                                            onChange={() => {}}
                                                        />
                                                    </div>
                                                )}
                                                <div className="flex-row w-100 small-gap">
                                                    <InputField
                                                        inputFieldId="effective-light-intensity-yellow"
                                                        label="Effektiv lysstyrke gul"
                                                        type="text"
                                                        valueSuffix="cd"
                                                        value={calculationResults?.effectiveIntensityYellow ?? ""}
                                                        readonly
                                                        onChange={() => {}}
                                                    />
                                                    <InputField
                                                        inputFieldId="calculated-range-yellow"
                                                        label="Beregnet rekkevidde gul"
                                                        type="text"
                                                        valueSuffix={activeUnit}
                                                        value={formatRangeNm(
                                                            calculationResults?.rangeYellow,
                                                            activeUnit === "m",
                                                            false
                                                        )}
                                                        readonly
                                                        onChange={() => {}}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </>
                    )}
                    {shouldUseCandelaForLED() && (
                        <>
                            <div className="flex-row small-gap">
                                <label className={`input-field-label`}>
                                    Lampe
                                    <NfsDropdown
                                        options={relevantCandelaCLs.map((candela) => candela.attributes.lampe)}
                                        allowEmpty
                                        selectedOption={selectedCandela?.attributes.lampe ?? null}
                                        setSelectedOption={selectCandela}
                                        placeholder="Velg lampe"
                                        searchable
                                    />
                                </label>
                                <InputField
                                    inputFieldId="lamp-type"
                                    label="Lampe type"
                                    type="text"
                                    readonly
                                    value={selectedCandela?.attributes.type ?? ""}
                                    onChange={() => {}}
                                />
                            </div>
                            <div className="flex-row small-gap">
                                <InputField
                                    inputFieldId="desired-distance"
                                    label={`Ønsket rekkevidde i ${activeUnit}`}
                                    type="number"
                                    min={0}
                                    step={"0.1"}
                                    valueSuffix={activeUnit}
                                    value={desiredDistanceDynamicInput}
                                    onChange={handleDesiredDistanceChange}
                                    w100
                                />
                            </div>
                            <div className="flex-row small-gap w-100">
                                <label className={`input-field-label`}>
                                    Fyrliste karakter
                                    <NfsDropdown
                                        options={relevantCharacterCLs.map((character) => character.attributes.kode)}
                                        allowEmpty
                                        selectedOption={selectedCharacter?.attributes.kode ?? null}
                                        setSelectedOption={selectCharacter}
                                        placeholder="Velg fyrliste karakter"
                                        searchable
                                    />
                                </label>
                            </div>
                            <div className="flex-row w-100 small-gap">
                                <label className={`input-field-label`}>
                                    Karakter
                                    <NfsDropdown
                                        disabled={selectedCharacter === null}
                                        options={relevantCharacterTimes.map(
                                            (character) => character.attributes.periode
                                        )}
                                        selectedOption={selectedCharacter?.attributes.periode ?? null}
                                        setSelectedOption={selectCharacterTime}
                                        placeholder="Velg karakter"
                                    />
                                </label>
                            </div>
                            <div className="flex-row w-100 small-gap">
                                <label className={`input-field-label`}>
                                    Bakgrunnsbelysning
                                    <NfsDropdown
                                        options={[BackgroundLight.NONE, BackgroundLight.MEDIUM, BackgroundLight.HIGH]}
                                        selectedOption={backgroundLight}
                                        setSelectedOption={(value) => {
                                            setBackgroundLight(value as BackgroundLight);
                                            setHasUnsavedChangesTrue();
                                        }}
                                        placeholder="Velg bakgrunnsbelysning"
                                    />
                                </label>
                                <InputField
                                    inputFieldId="shortest-active-time"
                                    label="Korteste aktive tid"
                                    type="text"
                                    value={shortestActiveTime}
                                    readonly
                                    onChange={() => {}}
                                />
                            </div>
                            <div className="flex-row w-100 small-gap">
                                <label className={`input-field-label`}>
                                    Lysfarge
                                    <NfsDropdown
                                        options={[
                                            LightCalculationLightColors.WHITE,
                                            LightCalculationLightColors.RED,
                                            LightCalculationLightColors.GREEN,
                                            LightCalculationLightColors.YELLOW,
                                        ]}
                                        allowEmpty
                                        readonly
                                        selectedOption={colorsToShow.length > 0 ? colorsToShow.join(", ") : lightColor}
                                        setSelectedOption={(value) => {
                                            setLightColor(value as LightCalculationLightColors);
                                            setHasUnsavedChangesTrue();
                                        }}
                                        placeholder="Velg lysfarge"
                                    />
                                </label>
                                <label className={`input-field-label`}>
                                    Lysgjennomgang
                                    <NfsDropdown
                                        options={[
                                            PassThroughMaterial.GLASS,
                                            PassThroughMaterial.PLASTIC,
                                            PassThroughMaterial.NONE,
                                        ]}
                                        selectedOption={passthroughMaterial}
                                        setSelectedOption={(value) => {
                                            setPassthroughMaterial(value as PassThroughMaterial);
                                            setHasUnsavedChangesTrue();
                                        }}
                                        placeholder="Velg lysgjennomgang"
                                    />
                                </label>
                            </div>
                            {(lightColor === LightCalculationLightColors.WHITE ||
                                (lightColor === null && colorsToShow.includes(LightCalculationLightColors.WHITE))) && (
                                <>
                                    <div className="flex-row w-100 small-gap">
                                        <InputField
                                            inputFieldId="light-intensity-white-min"
                                            label="Min lysstyrke Cd (Hvit)"
                                            type="text"
                                            readonly
                                            valueSuffix="cd"
                                            value={whiteMinCandela?.toString()}
                                            onChange={() => {}}
                                        />
                                        <InputField
                                            inputFieldId="light-intensity-white-max"
                                            label="Maks lysstyrke Cd (Hvit)"
                                            type="text"
                                            readonly
                                            valueSuffix="cd"
                                            value={whiteMaxCandela?.toString()}
                                            onChange={() => {}}
                                        />
                                    </div>

                                    <div className="flex-col small-gap">
                                        {selectedFeature && featureHasLight(selectedFeature) && (
                                            <div className="flex-row w-100 small-gap">
                                                <InputField
                                                    inputFieldId="light-intensity-white"
                                                    label="Dagens lystyrke hvit"
                                                    type="text"
                                                    valueSuffix="cd"
                                                    value={selectedFeature.attributes.lysstyrke_hvit ?? ""}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                                <InputField
                                                    inputFieldId="light-range-white"
                                                    label="Dagens rekkevidde hvit"
                                                    type="text"
                                                    valueSuffix={activeUnit}
                                                    value={formatRangeNm(
                                                        selectedFeature.attributes.rekkevidde_hvit,
                                                        activeUnit === "m",
                                                        false
                                                    )}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                            </div>
                                        )}
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="distance-white"
                                                label="Rekkevidde hvit"
                                                type="text"
                                                value={formatRangeOfRangeNm(rangeWhite, activeUnit === "m", true)}
                                                readonly
                                                onChange={() => {}}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {(lightColor === LightCalculationLightColors.RED ||
                                (lightColor === null && colorsToShow.includes(LightCalculationLightColors.RED))) && (
                                <>
                                    <div className="flex-row w-100 small-gap">
                                        <InputField
                                            inputFieldId="light-intensity-red-min"
                                            label="Min lysstyrke Cd (Rød)"
                                            type="text"
                                            readonly
                                            valueSuffix="cd"
                                            value={redMinCandela?.toString()}
                                            onChange={() => {}}
                                        />
                                        <InputField
                                            inputFieldId="light-intensity-red-max"
                                            label="Maks lysstyrke Cd (Rød)"
                                            type="text"
                                            readonly
                                            valueSuffix="cd"
                                            value={redMaxCandela?.toString()}
                                            onChange={() => {}}
                                        />
                                    </div>

                                    <div className="flex-col small-gap">
                                        {selectedFeature && featureHasLight(selectedFeature) && (
                                            <div className="flex-row w-100 small-gap">
                                                <InputField
                                                    inputFieldId="light-intensity-red"
                                                    label="Dagens lystyrke rød"
                                                    type="text"
                                                    valueSuffix="cd"
                                                    value={selectedFeature.attributes.lysstyrke_rod ?? ""}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                                <InputField
                                                    inputFieldId="light-range-red"
                                                    label="Dagens rekkevidde rød"
                                                    type="text"
                                                    valueSuffix={activeUnit}
                                                    value={formatRangeNm(
                                                        selectedFeature.attributes.rekkevidde_rod,
                                                        activeUnit === "m",
                                                        false
                                                    )}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                            </div>
                                        )}
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="distance-red"
                                                label="Rekkevidde rød"
                                                type="text"
                                                value={formatRangeOfRangeNm(rangeRed, activeUnit === "m", true)}
                                                readonly
                                                onChange={() => {}}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                            {(lightColor === LightCalculationLightColors.GREEN ||
                                (lightColor === null && colorsToShow.includes(LightCalculationLightColors.GREEN))) && (
                                <>
                                    <div className="flex-row w-100 small-gap">
                                        <InputField
                                            inputFieldId="light-intensity-green-min"
                                            label="Min lysstyrke Cd (Grønn)"
                                            type="text"
                                            readonly
                                            valueSuffix="cd"
                                            value={greenMinCandela?.toString()}
                                            onChange={() => {}}
                                        />
                                        <InputField
                                            inputFieldId="light-intensity-green-max"
                                            label="Maks lysstyrke Cd (Grønn)"
                                            type="text"
                                            readonly
                                            valueSuffix="cd"
                                            value={greenMaxCandela?.toString()}
                                            onChange={() => {}}
                                        />
                                    </div>

                                    <div className="flex-col small-gap">
                                        {selectedFeature && featureHasLight(selectedFeature) && (
                                            <div className="flex-row w-100 small-gap">
                                                <InputField
                                                    inputFieldId="light-intensity-green"
                                                    label="Dagens lystyrke grønn"
                                                    type="text"
                                                    valueSuffix="cd"
                                                    value={selectedFeature.attributes.lysstyrke_gronn ?? ""}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                                <InputField
                                                    inputFieldId="light-range-green"
                                                    label="Dagens rekkevidde grønn"
                                                    type="text"
                                                    valueSuffix={activeUnit}
                                                    value={formatRangeNm(
                                                        selectedFeature.attributes.rekkevidde_gronn,
                                                        activeUnit === "m",
                                                        false
                                                    )}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                            </div>
                                        )}
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="distance-green"
                                                label="Rekkevidde grønn"
                                                type="text"
                                                value={formatRangeOfRangeNm(rangeGreen, activeUnit === "m", true)}
                                                readonly
                                                onChange={() => {}}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            {(lightColor === LightCalculationLightColors.YELLOW ||
                                (lightColor === null && colorsToShow.includes(LightCalculationLightColors.YELLOW))) && (
                                <>
                                    <div className="flex-row w-100 small-gap">
                                        <InputField
                                            inputFieldId="light-intensity-yellow-min"
                                            label="Min lysstyrke Cd (Gul)"
                                            type="text"
                                            readonly
                                            valueSuffix="cd"
                                            value={yellowMinCandela?.toString()}
                                            onChange={() => {}}
                                        />
                                        <InputField
                                            inputFieldId="light-intensity-yellow-max"
                                            label="Maks lysstyrke Cd (Gul)"
                                            type="text"
                                            readonly
                                            valueSuffix="cd"
                                            value={yellowMaxCandela?.toString()}
                                            onChange={() => {}}
                                        />
                                    </div>

                                    <div className="flex-col small-gap">
                                        {selectedFeature && featureHasLight(selectedFeature) && (
                                            <div className="flex-row w-100 small-gap">
                                                <InputField
                                                    inputFieldId="light-intensity-yellow"
                                                    label="Dagens lystyrke gul"
                                                    type="text"
                                                    valueSuffix="cd"
                                                    value={selectedFeature.attributes.lysstyrke_gul ?? ""}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                                <InputField
                                                    inputFieldId="light-range-yellow"
                                                    label="Dagens rekkevidde gul"
                                                    type="text"
                                                    valueSuffix={activeUnit}
                                                    value={formatRangeNm(
                                                        selectedFeature.attributes.rekkevidde_gul,
                                                        activeUnit === "m",
                                                        false
                                                    )}
                                                    readonly
                                                    className="light-calculator-disabled-input"
                                                    onChange={() => {}}
                                                />
                                            </div>
                                        )}
                                        <div className="flex-row w-100 small-gap">
                                            <InputField
                                                inputFieldId="distance-yellow"
                                                label="Rekkevidde gul"
                                                type="text"
                                                value={formatRangeOfRangeNm(rangeYellow, activeUnit === "m", true)}
                                                readonly
                                                onChange={() => {}}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="flex-col w-100 small-gap">
                                {resultCandela.message !== "" && (
                                    <InputField
                                        inputFieldId="effective-light-intensity-candela-message"
                                        label="Feilmelding"
                                        type="text"
                                        isTextArea
                                        textAreaRows={2}
                                        value={resultCandela.message}
                                        readonly
                                        onChange={() => {}}
                                    />
                                )}
                                <InputField
                                    inputFieldId="effective-light-intensity-candela"
                                    label="Effektiv lysstyrke Cd (innstilling laterne)"
                                    type="text"
                                    valueSuffix="cd"
                                    value={resultCandela.effectiveIntensity.toFixed(2) ?? ""}
                                    readonly
                                    onChange={() => {}}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
            <div className="light-calculator-footer flex-col">
                <div className="flex-row small-gap">
                    {(!selectedFeature || !featureHasLight(selectedFeature)) && (
                        <NfsButton onClick={() => resetAndLoadValuesFromSelectedFeature(false)}>Nullstill</NfsButton>
                    )}
                    {selectedFeature && featureHasLight(selectedFeature) && (
                        <NfsButton
                            outlined
                            onClick={() => {
                                resetAndLoadValuesFromSelectedFeature(true);
                            }}
                        >
                            Last fra objekt
                        </NfsButton>
                    )}
                </div>
                {selectedFeature && featureHasLight(selectedFeature) && canSaveLightValues() && (
                    <NfsButton disabled={isSaving} onClick={saveLightValues}>
                        Lagre
                    </NfsButton>
                )}
            </div>
        </div>
    );
};

export default LightCalculator;
